import React, { useEffect, useRef, useState, useCallback } from "react";
import { Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Send, Lock, Hash, Users, Trash2, AtSign, Bell, BellOff, BellRing, Info, MessageCircle, X, Loader2, List, User as UserIcon } from "lucide-react";
import CertifiedBadge from "@/components/CertifiedBadge";
import FunnyTitleBadge from "@/components/FunnyTitleBadge";
import LifetimeMedal from "@/components/LifetimeMedal";
import FounderBadge from "@/components/FounderBadge";
import GoldBadge from "@/components/GoldBadge";
import SupporterBadge from "@/components/SupporterBadge";
import EmojiPicker from "@/components/EmojiPicker";
import GuestGate from "@/components/GuestGate";
import ImageUploadButton, { ImageThumbPreview, ChatImage } from "@/components/ImageUploadButton";
import VideoEmbed from "@/components/VideoEmbed";
import { pushSupported, pushPermission, subscribeToPush, unsubscribeFromPush, getPushSubscription, clearAppBadge } from "@/lib/push";
import PushPlayStoreNotice from "@/components/PushPlayStoreNotice";
import LanguageFlags from "@/components/LanguageFlags";
import useChatSocket from "@/hooks/useChatSocket";

function NotificationPermBtn() {
  const { lang } = useLang();
  const tr = {
    sk: { on: "Notifikácie ON", denied: "Blokované", enable: "Povoliť oznámenia", off: "Vypnúť", hint: "V nastaveniach mobilu povol notifikácie pre Tigik" },
    en: { on: "Notifications ON", denied: "Blocked", enable: "Enable notifications", off: "Turn off", hint: "Allow notifications for Tigik in your phone settings" },
    cs: { on: "Notifikace ON", denied: "Blokováno", enable: "Povolit oznámení", off: "Vypnout", hint: "V nastavení mobilu povol notifikace pro Tigik" },
    de: { on: "Benachrichtigungen AN", denied: "Blockiert", enable: "Benachrichtigungen aktivieren", off: "Ausschalten", hint: "Erlaube Benachrichtigungen für Tigik in den Telefon­einstellungen" },
    pl: { on: "Powiadomienia WŁ.", denied: "Zablokowane", enable: "Włącz powiadomienia", off: "Wyłącz", hint: "Zezwól na powiadomienia dla Tigik w ustawieniach telefonu" },
    fr: {
  on: "Notifications ON",
  denied: "Bloquées",
  enable: "Autoriser les notifications",
  off: "Désactiver",
  hint: "Dans les paramètres de ton mobile, autorise les notifications pour Tigik"
},
  }[lang] || {};
  const [perm, setPerm] = useState(pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    getPushSubscription().then((s) => setSubscribed(!!s));
    clearAppBadge();
  }, []);

  if (!pushSupported()) return null;

  if (perm === "granted" && subscribed) {
    return (
      <button
        data-testid="chat-notif-granted"
        onClick={async () => {
          if (busy) return;
          setBusy(true);
          try { await unsubscribeFromPush(); setSubscribed(false); } catch (_) {}
          setBusy(false);
        }}
        title={tr.off}
        className="inline-flex items-center gap-1 text-[10px] font-mono text-green-400 hover:text-zinc-500 transition-colors"
      >
        <BellRing className="w-3 h-3" /> {tr.on}
      </button>
    );
  }
  if (perm === "denied") {
    return (
      <span data-testid="chat-notif-denied" title={tr.hint} className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-500">
        <BellOff className="w-3 h-3" /> {tr.denied}
      </span>
    );
  }
  return (
    <button
      data-testid="chat-notif-enable"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await subscribeToPush();
          setPerm(pushPermission());
          setSubscribed(true);
        } catch (e) {
          setPerm(pushPermission());
        }
        setBusy(false);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono bg-gold-500/15 border border-gold-700 text-gold-400 hover:bg-gold-500/25 disabled:opacity-50"
    >
      <Bell className="w-3 h-3" /> {busy ? "…" : tr.enable}
    </button>
  );
}

// Render a chat line with @mentions highlighted in gold
function renderTextWithMentions(text, currentUserName) {
  // Guard against image-only / video-only messages where text can be null / undefined /
  // empty. Previously this crashed the whole Community route → black screen.
  if (text == null || text === "") return null;
  const safeText = String(text);
  const re = /@(?:"([^"]+)"|([A-Za-zÀ-ž0-9_\-.]+))/g;
  const parts = [];
  let last = 0; let m;
  while ((m = re.exec(safeText)) !== null) {
    if (m.index > last) parts.push({ t: "txt", v: safeText.slice(last, m.index) });
    const name = m[1] || m[2];
    const isMe = currentUserName && name.toLowerCase() === currentUserName.toLowerCase();
    parts.push({ t: "mention", v: name, isMe });
    last = m.index + m[0].length;
  }
  if (last < safeText.length) parts.push({ t: "txt", v: safeText.slice(last) });
  return parts.map((p, i) =>
    p.t === "mention"
      ? <span key={i} className={`font-semibold px-1 rounded ${p.isMe ? "bg-gold-500/30 text-gold-300" : "text-gold-400"}`}>@{p.v}</span>
      : <React.Fragment key={i}>{p.v}</React.Fragment>
  );
}

export default function Community() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const [channels, setChannels] = useState([]);
  const [onlineByChannel, setOnlineByChannel] = useState({});
  const [active, setActive] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("ch") || "pipefitters";
    } catch (e) { return "pipefitters"; }
  });
  // Keep `active` in sync with the ?ch= query param on subsequent navigations
  // (e.g. push-notification tap while the Community page is already open).
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search);
      const wanted = p.get("ch");
      if (wanted && wanted !== active) setActive(wanted);
    } catch (_) {}
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [chatImage, setChatImage] = useState(null);
  const [chatVideoUrl, setChatVideoUrl] = useState("");
  const [showVideoField, setShowVideoField] = useState(false);
  const [unread, setUnread] = useState({ channels: {}, total: 0 });
  // @mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState(null); // null when not active
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mobileChannelsOpen, setMobileChannelsOpen] = useState(true);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  // Direct Message modal: { id, name, picture } | null
  const [dmTarget, setDmTarget] = useState(null);

  // Channels with auto-trim (must match backend AUTO_TRIM_CHANNELS)
  const AUTO_TRIM_CHANNELS = new Set(["general", "welders", "pripravari", "news"]);
  const isAutoTrim = AUTO_TRIM_CHANNELS.has(active);

  // Ask notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/chat/channels").then((r) => setChannels(r.data)).catch(() => {});
  }, [user]);

  // Per-channel online presence — heartbeat with active channel + polling
  useEffect(() => {
    if (!user) return;
    const ping = () => api.post("/presence/heartbeat", { channel: active }).catch(() => {});
    const fetchOnline = () =>
      api.get("/presence/online").then((r) => setOnlineByChannel(r.data.per_channel || {})).catch(() => {});
    ping();
    fetchOnline();
    const idPing = setInterval(ping, 30000);
    const idFetch = setInterval(fetchOnline, 30000);
    return () => { clearInterval(idPing); clearInterval(idFetch); };
  }, [user, active]);

  const loadUnread = useCallback(async () => {
    try {
      const r = await api.get("/chat/notifications/unread");
      setUnread(r.data);
    } catch { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const r = await api.get(`/chat/messages/${active}`);
      const list = r.data;
      // Notify only for new messages
      if (user && seenIdsRef.current.size > 0) {
        list.forEach((m) => {
          if (seenIdsRef.current.has(m.id)) return;
          if (m.user_id === user.id) return;
          const mentionsMe = (m.mentioned_user_ids || []).includes(user.id);
          // Show desktop / mobile notification ONLY for @mentions
          if (mentionsMe && "Notification" in window && Notification.permission === "granted") {
            try {
              new Notification(`Tigik · ${m.user_name}`, {
                body: m.text,
                icon: "/tigik-logo.png",
                tag: m.id,
                requireInteraction: true,
                vibrate: [120, 60, 120],
              });
            } catch { /* ignore */ }
          }
          // Sound: ONLY on @mention (subtle ping disabled per user preference — too noisy)
          if (mentionsMe) {
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              const o = ctx.createOscillator();
              const g = ctx.createGain();
              o.connect(g); g.connect(ctx.destination);
              o.frequency.value = 880;
              g.gain.value = 0.10;
              o.start(); o.stop(ctx.currentTime + 0.25);
            } catch { /* ignore */ }
          }
        });
      }
      list.forEach((m) => seenIdsRef.current.add(m.id));
      setMessages(list);
      // mark this channel as read
      try {
        await api.post(`/chat/mark-read/${active}`);
        // tell global header badge to refresh immediately
        try { window.dispatchEvent(new CustomEvent("chat:read")); } catch { /* ignore */ }
      } catch { /* ignore */ }
      loadUnread();
    } catch { /* ignore */ }
  }, [active, user, loadUnread]);

  // WebSocket: real-time message + delete events. When connected, polling drops to 30s.
  const tokenForWs = typeof window !== "undefined" ? localStorage.getItem("wb_token") : null;
  const { connected: wsConnected } = useChatSocket(
    user && user.is_subscribed ? active : null,
    tokenForWs,
    {
      onNewMessage: (msg) => {
        if (!msg || !msg.id) return;
        if (seenIdsRef.current.has(msg.id)) return;
        seenIdsRef.current.add(msg.id);
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        // Mark channel as read (fire-and-forget) so the inbox badge stays accurate
        api.post(`/chat/mark-read/${active}`).catch(() => {});
        try { window.dispatchEvent(new CustomEvent("chat:read")); } catch { /* ignore */ }
      },
      onDeleteMessage: (id) => {
        if (!id) return;
        setMessages((prev) => prev.filter((m) => m.id !== id));
      },
    }
  );

  useEffect(() => {
    if (!user || !user.is_subscribed) return;
    seenIdsRef.current = new Set(); // reset per channel
    loadMessages();
    if (pollRef.current) clearInterval(pollRef.current);
    // When WS is connected, only do a "safety net" poll every 30s (catches any missed event).
    // When WS is offline (cold-start or failed), fall back to fast 3.5s polling.
    const interval = wsConnected ? 30000 : 3500;
    pollRef.current = setInterval(loadMessages, interval);
    return () => clearInterval(pollRef.current);
  }, [active, user, loadMessages, wsConnected]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  // @mention autocomplete handler
  const onTextChange = async (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart || v.length;
    const before = v.slice(0, caret);
    const m = before.match(/(^|\s)@([A-Za-zÀ-ž0-9_\-.]{0,40})$/);
    if (m) {
      const q = m[2];
      setMentionQuery(q);
      try {
        const r = await api.get("/chat/users", { params: { q } });
        setMentionResults(r.data || []);
        setMentionIdx(0);
      } catch { setMentionResults([]); }
    } else {
      setMentionQuery(null);
      setMentionResults([]);
    }
  };

  const insertMention = (name) => {
    const caret = inputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const after = text.slice(caret);
    const withoutPartial = before.replace(/@([A-Za-zÀ-ž0-9_\-.]*)$/, "");
    const token = /\s/.test(name) ? `@"${name}" ` : `@${name} `;
    const next = withoutPartial + token + after;
    setText(next);
    setMentionQuery(null);
    setMentionResults([]);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onKeyDown = (e) => {
    if (mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionResults.length); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionResults.length) % mentionResults.length); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionResults[mentionIdx].name);
        return;
      }
      if (e.key === "Escape") { setMentionQuery(null); setMentionResults([]); return; }
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const vurl = chatVideoUrl.trim();
    if (!text.trim() && !chatImage && !vurl) return;
    if (vurl && !/^https?:\/\/(www\.|m\.)?(youtube\.com|youtu\.be|vimeo\.com)\//i.test(vurl)) {
      alert("Video URL musí byť YouTube alebo Vimeo odkaz.");
      return;
    }
    try {
      await api.post("/chat/send", { channel: active, text, image_base64: chatImage, video_url: vurl || null });
      setText("");
      setChatImage(null);
      setChatVideoUrl("");
      setShowVideoField(false);
      setMentionQuery(null);
      loadMessages();
    } catch { /* ignore */ }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Zmazať túto správu?")) return;
    try { await api.delete(`/chat/messages/${id}`); loadMessages(); } catch { /* ignore */ }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div data-testid="community-guest" className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <span className="wb-tag !border-gold-600 !text-gold-500">KOMUNITA</span>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2 mb-4">{t.chat.paywallTitle}</h1>
        <GuestGate customMsg="Komunitný chat je len pre prihlásených zváračov, potrubárov a pripravárov. Po registrácii sa môžeš pridať do diskusií, klásť otázky odborníkom a hľadať prácu." />
        <div className="mt-6 border border-zinc-800 bg-[var(--wb-surface)] p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">Ukážka — bot odpoveď</div>
          <div className="space-y-3 text-sm">
            <div className="flex gap-2"><span className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-mono text-gold-500 flex-shrink-0">?</span>
              <div className="bg-zinc-900 border border-zinc-800 p-2.5">Aké parametre nastaviť pre orbital DN50 nerez?</div>
            </div>
            <div className="flex gap-2"><span className="w-8 h-8 bg-gold-500/15 border border-gold-600 flex items-center justify-center text-xs font-mono text-gold-400 flex-shrink-0">B</span>
              <div className="bg-gold-500/10 border border-gold-700/40 p-2.5 text-zinc-200">Ahoj zvedavec! 👋 Pridaj sa k nám a získaj plný prístup ku všetkým kanálom — pipefitters, orbital, tig, welders aj general. <strong>A nezabudni pozvať kamošov!</strong> 🤝</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user.is_subscribed && user.role !== "admin") {
    return (
      <div data-testid="community-paywall" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mx-auto border border-zinc-800 bg-[var(--wb-surface)] p-12 text-center wb-noise relative overflow-hidden">
          <Lock className="w-12 h-12 text-gold-500 mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-display font-black text-4xl mb-3">{t.chat.paywallTitle}</h1>
          <p className="text-zinc-400 mb-8">{t.chat.paywallSub}</p>
          <Link to="/pricing" data-testid="paywall-cta" className="wb-btn-primary inline-block px-8 py-3 text-sm">
            {t.chat.paywallCta}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="community-page" className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-12 gap-4 h-[calc(100dvh-8rem)] min-h-[400px]">
      <aside className="hidden lg:flex lg:col-span-3 border border-zinc-800 bg-[var(--wb-surface)] flex-col" data-testid="channel-list">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <span className="font-display font-bold uppercase tracking-wider text-sm">{t.chat.channels}</span>
          {unread.total > 0 && (
            <span className="wb-tag !text-gold-300 !border-gold-600 inline-flex items-center gap-1" data-testid="mention-total-badge">
              <Bell className="w-3 h-3" /> {unread.total}
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {channels.map((c) => {
            const u = unread.channels?.[c.id] || 0;
            const isNews = c.id === "news";
            return (
              <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setActive(c.id)}
                className={`w-full text-left p-0 text-sm border-l-2 transition-colors flex flex-col overflow-hidden ${active === c.id ? "border-gold-500 bg-zinc-900/50 text-white" : isNews ? "border-gold-700/60 bg-gold-950/20 text-gold-200 hover:bg-gold-950/40" : "border-transparent text-zinc-400 hover:text-white hover:border-gold-700/40"}`}>
                {c.image && (
                  <div className="relative w-full h-16 overflow-hidden">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover opacity-50 hover:opacity-70 transition-opacity" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    {u > 0 && (
                      <span data-testid={`channel-${c.id}-badge`} className="absolute top-1.5 right-1.5 text-[10px] font-mono bg-gold-500 text-black px-1.5 py-0.5 rounded-full">{u}</span>
                    )}
                  </div>
                )}
                <div className="p-2 -mt-1 flex items-start justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold flex items-center gap-1">
                      {isNews ? <span className="text-gold-400 mr-0.5" aria-hidden>📢</span> : <Hash className="w-3 h-3 text-gold-500" />}
                      {t.chat.channelNames?.[c.id] || c.name}
                      {isNews && <span className="ml-1 text-[8px] font-mono uppercase tracking-wider text-gold-500/80">{t.chat.adminOnlyLabel || "ADMIN ONLY"}</span>}
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mt-0.5">{t.chat.channelDescs?.[c.id] || c.desc}</div>
                  </div>
                  {(onlineByChannel[c.id] || 0) > 0 && (
                    <span data-testid={`channel-${c.id}-online`} className="inline-flex items-center gap-1 text-[10px] font-mono text-green-400 whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                      {onlineByChannel[c.id]}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="lg:col-span-9 border border-zinc-800 bg-[var(--wb-surface)] flex flex-col relative h-full min-h-0 overflow-hidden" data-testid="chat-room">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between gap-2">
          <button
            data-testid="mobile-channel-toggle"
            onClick={() => setMobileChannelsOpen((v) => !v)}
            className="lg:hidden wb-btn-ghost px-2.5 py-1.5 text-xs font-display font-bold uppercase tracking-wider inline-flex items-center gap-1.5 flex-shrink-0 relative"
            aria-label={t.chat.channels}
          >
            <List className="w-4 h-4 text-gold-500" />
            <span className="hidden xs:inline">{t.chat.channels}</span>
            {unread.total > 0 && (
              <span className="ml-1 text-[10px] font-mono bg-gold-500 text-black px-1.5 py-0.5 rounded">{unread.total}</span>
            )}
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Hash className="w-4 h-4 text-gold-500 hidden lg:inline flex-shrink-0" />
            <span className="font-display font-bold text-base sm:text-lg truncate">
              {t.chat.channelNames?.[active] || channels.find((c) => c.id === active)?.name || active}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationPermBtn />
            <span className="wb-tag !text-green-500 !border-green-700 hidden sm:inline-flex">{t.common.live}</span>
          </div>
        </div>

        {/* Mobile channel picker — full list with small images. Open by default on mount;
            closes after selecting a channel; reopens via the "List" button in header. */}
        {mobileChannelsOpen && (
          <div className="lg:hidden border-b border-zinc-800 bg-zinc-950 flex-1 overflow-y-auto" data-testid="mobile-channels-panel">
            <div className="p-2 space-y-1.5">
              <PushPlayStoreNotice compact />
              {channels.map((c) => {
                const u = unread.channels?.[c.id] || 0;
                const localizedName = t.chat.channelNames?.[c.id] || c.name;
                const online = onlineByChannel[c.id] || 0;
                const isNews = c.id === "news";
                return (
                  <button key={c.id} data-testid={`mobile-channel-${c.id}`}
                    onClick={() => { setActive(c.id); setMobileChannelsOpen(false); }}
                    className={`w-full text-left p-0 text-sm border-l-2 transition-colors flex flex-col overflow-hidden ${active === c.id ? "border-gold-500 bg-zinc-900/50 text-white" : isNews ? "border-gold-700/60 bg-gold-950/20 text-gold-200" : "border-transparent text-zinc-400 hover:text-white hover:border-gold-700/40"}`}>
                    {c.image && (
                      <div className="relative w-full h-12 overflow-hidden">
                        <img src={c.image} alt={localizedName} className="w-full h-full object-cover opacity-50" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                        {u > 0 && (
                          <span data-testid={`mobile-channel-${c.id}-badge`} className="absolute top-1 right-1 text-[10px] font-mono bg-gold-500 text-black px-1.5 py-0.5 rounded-full">{u}</span>
                        )}
                      </div>
                    )}
                    <div className="px-2.5 py-1.5 -mt-1 flex items-start justify-between gap-2 w-full">
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold flex items-center gap-1 truncate">
                          {isNews ? <span className="text-gold-400 flex-shrink-0" aria-hidden>📢</span> : <Hash className="w-3 h-3 text-gold-500 flex-shrink-0" />}
                          {localizedName}
                          {isNews && <span className="text-[8px] font-mono uppercase tracking-wider text-gold-500/80 flex-shrink-0">{t.chat.adminOnlyLabel || "ADMIN"}</span>}
                        </div>
                        <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mt-0.5 truncate">{t.chat.channelDescs?.[c.id] || c.desc}</div>
                      </div>
                      {online > 0 && (
                        <span data-testid={`mobile-channel-${c.id}-online`} className="inline-flex items-center gap-1 text-[10px] font-mono text-green-400 whitespace-nowrap flex-shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                          {online}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div ref={scrollRef} className={`flex-1 overflow-y-auto p-4 space-y-2 ${mobileChannelsOpen ? "hidden lg:block" : ""}`} data-testid="chat-messages">
          {isAutoTrim && messages.length > 0 && (
            <div data-testid="autotrim-banner" className="mb-3 flex items-start gap-2 p-2.5 border-l-2 border-amber-500 bg-amber-500/5 text-xs text-zinc-400">
              <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="text-amber-400">Verejný kanál:</strong> staršie správy sa po 100ke mažú automaticky. Pre dlhšiu súkromnú komunikáciu klikni na <strong>meno alebo avatar</strong> užívateľa — máš svoju schránku v <Link to="/profile" className="text-gold-400 underline">Profile</Link>.
              </div>
            </div>
          )}
          {messages.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              <Users className="w-10 h-10 mx-auto mb-2" strokeWidth={1.2} />
              <p className="text-sm font-mono uppercase">No messages yet</p>
              <p className="text-xs mt-2 text-zinc-500">Tip: napíš @ aby si niekoho označil/označila — len jemu/jej zazvoní mobil.</p>
            </div>
          )}
          {messages.map((m) => {
            const mentionsMe = (m.mentioned_user_ids || []).includes(user.id);
            const canDelete = m.user_id === user.id || user.role === "admin";
            return (
              <div key={m.id} className={`flex gap-3 group p-2 rounded ${mentionsMe ? "bg-gold-500/10 border-l-2 border-gold-500" : ""}`} data-testid={`chat-msg-${m.id}`}>
                <button
                  type="button"
                  data-testid={`chat-msg-${m.id}-avatar`}
                  onClick={() => {
                    if (m.user_id === user.id) return; // can't view your own DM-modal
                    nav(`/u/${m.user_id}`);
                  }}
                  title={m.user_id === user.id ? m.user_name : `Otvoriť profil — ${m.user_name}`}
                  className="relative w-8 h-8 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-gold-500 transition-all"
                  disabled={m.user_id === user.id}
                >
                  <div className={`w-8 h-8 bg-zinc-800 ${m.user_certified ? "border border-yellow-500/70 ring-1 ring-yellow-400/30" : "border border-zinc-700"} flex items-center justify-center overflow-hidden`}>
                    {m.user_picture ? (
                      <img src={m.user_picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-mono uppercase text-gold-500">{m.user_name?.[0] || "?"}</span>
                    )}
                  </div>
                  {m.user_certified && (
                    <div className="absolute -bottom-1 -right-1">
                      <CertifiedBadge size="xs" certificateNo={m.user_certificate_no} />
                    </div>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid={`chat-msg-${m.id}-name`}
                      onClick={() => {
                        if (m.user_id === user.id) return;
                        insertMention(m.user_name);
                      }}
                      disabled={m.user_id === user.id}
                      title={m.user_id === user.id ? "" : `Odpovedať @${m.user_name}`}
                      className={`font-display font-bold text-sm ${m.user_id === user.id ? "" : "hover:text-gold-400 transition-colors cursor-pointer"}`}
                    >
                      {m.user_name}
                    </button>
                    {m.user_id !== user.id && (
                      <button
                        type="button"
                        data-testid={`chat-msg-${m.id}-profile`}
                        onClick={() => nav(`/u/${m.user_id}`)}
                        title={`Profil ${m.user_name}`}
                        className="text-zinc-500 hover:text-gold-400 transition-colors p-0.5 -my-0.5"
                      >
                        <UserIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {m.user_preferred_languages?.length > 0 && (
                      <LanguageFlags languages={m.user_preferred_languages} size="sm" />
                    )}
                    {m.user_certified && <CertifiedBadge size="sm" certificateNo={m.user_certificate_no} />}
                    {m.user_certified && m.user_certified_since && <LifetimeMedal since={m.user_certified_since} lang={lang} size="sm" />}
                    {m.user_plan_tier === "gold" && <GoldBadge size="sm" />}
                    {m.user_supporter && <SupporterBadge lang={lang} />}
                    {m.is_founder_post && <FounderBadge size="sm" />}
                    {m.user_custom_title && <FunnyTitleBadge title={m.user_custom_title} color={m.user_custom_title_color} />}
                    <span className="text-[10px] font-mono text-zinc-600">{new Date(m.ts).toLocaleTimeString()}</span>
                    {mentionsMe && <span className="wb-tag !text-gold-300 !border-gold-600 !text-[9px]">MENTION</span>}
                  </div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">
                    {renderTextWithMentions(m.text, user.name)}
                  </div>
                  {m.image && <ChatImage src={m.image} />}
                  {m.video_url && <VideoEmbed url={m.video_url} />}
                </div>
                {canDelete && (
                  <button onClick={() => deleteMessage(m.id)} data-testid={`chat-msg-${m.id}-delete`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-400 self-start">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {active === "news" && user?.role !== "admin" ? (
          <div className={`p-4 border-t border-gold-700/40 bg-gradient-to-r from-gold-950/30 to-zinc-950 text-center pb-[max(env(safe-area-inset-bottom),16px)] ${mobileChannelsOpen ? "hidden lg:block" : ""}`} data-testid="news-readonly-notice">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gold-400">
              <Lock className="w-3.5 h-3.5" /> {t.chat.newsReadOnly || "Read only — iba Sauberino(Admin) postuje oznamy. Push notifikáciu dostaneš automaticky."}
            </div>
          </div>
        ) : (
        <form onSubmit={send} className={`p-3 border-t border-zinc-800 flex gap-2 items-end relative pb-[max(env(safe-area-inset-bottom),12px)] bg-[var(--wb-surface)] ${mobileChannelsOpen ? "hidden lg:flex" : ""}`} data-testid="chat-form">
          {/* Mention autocomplete */}
          {mentionQuery !== null && mentionResults.length > 0 && (
            <div className="absolute bottom-full left-3 mb-1 w-72 max-h-56 overflow-y-auto bg-[var(--wb-bg)] border border-gold-700/50 shadow-xl z-20" data-testid="mention-dropdown">
              {mentionResults.map((r, idx) => (
                <button key={r.id} type="button" onClick={() => insertMention(r.name)}
                  data-testid={`mention-option-${r.id}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${idx === mentionIdx ? "bg-gold-500/20 text-white" : "text-zinc-300 hover:bg-zinc-800"}`}>
                  <AtSign className="w-3.5 h-3.5 text-gold-500" />
                  <span className="font-display font-bold">{r.name}</span>
                </button>
              ))}
            </div>
          )}
          {chatImage && (
            <div className="absolute bottom-full left-3 mb-2 z-10">
              <ImageThumbPreview src={chatImage} onClear={() => setChatImage(null)} />
            </div>
          )}
          {/* Video URL field — available in any channel to any subscribed user */}
          {showVideoField && (
            <div className="absolute bottom-full left-3 right-3 mb-2 z-10 bg-[var(--wb-bg)] border border-gold-700/50 p-2 flex gap-2 items-center" data-testid="chat-video-field">
              <span className="text-[10px] font-mono text-gold-400 uppercase whitespace-nowrap">🎬 Video URL:</span>
              <input
                type="url"
                value={chatVideoUrl}
                onChange={(e) => setChatVideoUrl(e.target.value)}
                placeholder="https://youtu.be/... alebo https://vimeo.com/..."
                className="wb-input flex-1 min-w-0 px-2 py-1 text-xs font-mono"
                data-testid="chat-video-url"
              />
              <button type="button" onClick={() => { setChatVideoUrl(""); setShowVideoField(false); }} className="text-zinc-500 hover:text-red-400 text-xs px-2">✕</button>
            </div>
          )}
          <ImageUploadButton onPicked={setChatImage} testid="chat-image-btn" />
          {(() => {
            const tier = (user?.plan_tier || "").toLowerCase();
            const isAdmin = (user?.role || "").toLowerCase() === "admin";
            const canPostVideo = isAdmin || ["active", "pro", "gold", "excellent"].includes(tier);
            if (!canPostVideo) return null;
            return (
              <button
                type="button"
                onClick={() => setShowVideoField(!showVideoField)}
                data-testid="chat-video-btn"
                title="Pridať YouTube/Vimeo video (ACTIVE / PRO / GOLD)"
                className={`flex-shrink-0 px-3 py-2 text-xs border min-h-[44px] inline-flex items-center justify-center ${
                  showVideoField || chatVideoUrl ? "border-gold-500 text-gold-300 bg-gold-950/30" : "border-zinc-700 text-zinc-400 hover:border-gold-700 hover:text-gold-400"
                }`}
              >
                🎬
              </button>
            );
          })()}
          <input ref={inputRef} data-testid="chat-input" value={text}
            onChange={onTextChange} onKeyDown={onKeyDown}
            placeholder={`${t.chat.placeholder}  ·  @meno`}
            className="wb-input flex-1 min-w-0 px-3 py-2 text-sm min-h-[44px]" />
          <EmojiPicker testid="chat-emoji" onPick={(e) => setText(text + e)} />
          <button data-testid="chat-send-btn" type="submit" disabled={!text.trim() && !chatImage && !chatVideoUrl.trim()}
            className="wb-btn-primary flex-shrink-0 px-3 sm:px-4 py-2 text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50 min-h-[44px] min-w-[44px]"
            aria-label={t.chat.send}>
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">{t.chat.send}</span>
          </button>
        </form>
        )}
      </section>
      {dmTarget && <DMModal target={dmTarget} onClose={() => setDmTarget(null)} />}
    </div>
  );
}

function DMModal({ target, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/dm/send", { to_user_id: target.id, text: text.trim() });
      setSent(true);
      setText("");
      setTimeout(onClose, 1500);
    } catch (e2) {
      const detail = e2.response?.data?.detail || "Chyba pri odoslaní";
      alert(detail);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="dm-modal">
        <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-800 border border-zinc-700 overflow-hidden">
            {target.picture ? <img src={target.picture} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center text-xs font-display font-bold text-zinc-400 uppercase">{target.name?.[0]}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-gold-500" />
              Osobná správa
            </div>
            <div className="text-xs text-zinc-500 truncate">pre <strong>{target.name}</strong></div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3" data-testid="dm-modal-form">
          {sent ? (
            <div className="text-center py-6 text-green-400 text-sm">
              ✓ Správa odoslaná. Odpoveď uvidíš v Profile → Schránka.
            </div>
          ) : (
            <>
              <textarea data-testid="dm-modal-text" value={text} onChange={(e) => setText(e.target.value)}
                autoFocus rows={4} maxLength={2000} placeholder="Napíš osobnú správu…"
                className="wb-input w-full px-3 py-2 text-sm" />
              <div className="flex items-center gap-2 justify-between">
                <div className="text-[10px] font-mono text-zinc-600">
                  Doručené do schránky v Profile · {target.name} dostane oznámenie
                </div>
                <div className="flex items-center gap-2">
                  <EmojiPicker testid="dm-emoji" onPick={(e) => setText(text + e)} />
                  <button type="submit" disabled={busy || !text.trim()} data-testid="dm-modal-send"
                    className="wb-btn-primary px-4 py-2 text-sm inline-flex items-center gap-1.5">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Odoslať
                  </button>
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
